// ── Server-backed storage (with localStorage write-through cache) ──
//
// Drop-in for `localStorage.{getItem, setItem}` for keys that need to
// survive (a) localStorage quota evictions, (b) browser switches,
// (c) device switches.
//
// Architecture:
//   - Reads stay synchronous via localStorage cache (no UI churn).
//   - Writes hit localStorage immediately + queue a debounced server PUT.
//   - On app boot, call `hydrate(key)` once per migrated key — async
//     fetches the server value and overwrites localStorage if the server
//     copy is newer.
//
// Server: POST /api/kv/:key { value } / GET /api/kv/:key → { value, ts }
// Backed by Firestore collection `userKv` per server/routes/kv.js.
//
// Used by:
//   - useCanvasMode.js     (IDEA_GRAPH_VERSIONS)
//   - MemoryLayer.js       (IDEA_MEMORY_SESSIONS)
//   - TemplateStore.js     (THOUGHT_TEMPLATES)
//
// NOT used for UI prefs (chaos_level, sidebar_collapsed, etc.) or
// auth tokens (PAT_KEY) — those stay in plain localStorage.

import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

// In-memory metadata: last successful server timestamp per key, so we
// can avoid pointlessly re-PUTting unchanged data and so hydrate can
// skip if our local copy is fresh.
const lastServerTs = new Map(); // key → ISO timestamp string

// Debounced write queue — coalesces rapid setItem calls (e.g. version
// snapshots fired on every node mutation) into one server round-trip.
const pendingWrites = new Map(); // key → { value, timer }
const DEBOUNCE_MS = 800;

function flushWrite(key) {
  const entry = pendingWrites.get(key);
  if (!entry) return;
  pendingWrites.delete(key);
  authFetch(`${API_URL}/api/kv/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value: entry.value }),
  })
    .then(r => r.ok ? r.json() : null)
    .then(j => {
      if (j?.ok) lastServerTs.set(key, new Date().toISOString());
    })
    .catch(() => { /* offline / 503 — localStorage still has the value */ });
}

// Synchronous read — returns whatever localStorage has now.
// Use this anywhere the existing code calls localStorage.getItem(key).
function get(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? null : raw;
  } catch { return null; }
}

// Synchronous write to localStorage + debounced server PUT.
// Use this anywhere the existing code calls localStorage.setItem(key, value).
function set(key, value) {
  // Local write first (sync, fast, what the rest of the app expects)
  try { localStorage.setItem(key, value); } catch { /* quota — server still gets it */ }

  // Parse the value so we can ship it as JSON (server stores as object)
  let parsed;
  try { parsed = JSON.parse(value); } catch { parsed = value; }

  // Coalesce — if a write is already pending, replace the value; otherwise
  // schedule a flush.
  const existing = pendingWrites.get(key);
  if (existing) {
    existing.value = parsed;
    return;
  }
  const timer = setTimeout(() => flushWrite(key), DEBOUNCE_MS);
  pendingWrites.set(key, { value: parsed, timer });
}

// Async hydrate — call once at app boot per migrated key. If the server
// has a value AND our local cache is empty/older, write the server value
// into localStorage so the rest of the app picks it up on next read.
async function hydrate(key) {
  try {
    const r = await authFetch(`${API_URL}/api/kv/${encodeURIComponent(key)}`);
    if (!r.ok) return null;
    const { value, ts } = await r.json();
    if (value == null) return null;
    lastServerTs.set(key, ts);
    // If localStorage already has something with a fresher mtime we'd
    // rather not clobber it. We don't track local mtimes per key, so the
    // simple rule: only overwrite if local is empty. That's correct for
    // a fresh-browser scenario (which is the whole point of hydrate);
    // for a same-browser reload, local already has the latest from the
    // last setItem.
    const local = get(key);
    if (local == null) {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      try { localStorage.setItem(key, serialized); } catch { /* quota */ }
    }
    return value;
  } catch {
    return null;
  }
}

// Force-flush any pending writes (e.g. before unload). Best-effort; the
// `keepalive` flag lets the request complete after the page is gone.
function flushAllPending() {
  for (const [key, entry] of pendingWrites.entries()) {
    clearTimeout(entry.timer);
    pendingWrites.delete(key);
    try {
      const url = `${API_URL}/api/kv/${encodeURIComponent(key)}`;
      // navigator.sendBeacon doesn't carry our auth header, so just fire-and-forget fetch
      authFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: entry.value }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* nothing to do */ }
  }
}

// Wire flush on page unload so the last debounced write isn't lost
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushAllPending);
}

export const serverStorage = { get, set, hydrate, flushAllPending };
export default serverStorage;
